/*
 * @Author: Ryan
 * @Date: 2026-01-26
 * @Description: Explore Our AI Tools section with grid layout and carousel
 */
import Image from 'next/image';
import Link from 'next/link';
import React, { useRef, useState } from 'react';

import {
  avatarVideoProURL,
  videoGenerateURL,
  videoTranslateURL,
} from '@/utils/routeConstants';

interface Tool {
  id: string;
  imageUrl: string;
  title: string;
  description: string;
  link: string;
}

const ExploreToolsSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const tools: Tool[] = [
    {
      id: 'video-translator',
      imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-translator.png',
      title: 'AI Video Translator',
      description:
        'Translate videos into multiple languages with natural voice, accurate lip sync, and smooth subtitles. Perfect for global marketing, training content, and social videos—no reshooting required.',
      link: videoTranslateURL,
    },
    {
      id: 'video-generator',
      imageUrl: 'https://assets.falcocut.ai/tools/common/ai-video-generator.png',
      title: 'AI Video Generator',
      description:
        'Turn images or ideas into high-quality videos in minutes. Create product demos, social ads, and short-form content with clean visuals and professional results—fast and effortless.',
      link: videoGenerateURL,
    },
    {
      id: 'ai-avatar',
      imageUrl: 'https://assets.falcocut.ai/tools/common/ai-avatar-generator.png',
      title: 'AI Avatar Generator',
      description:
        'Create realistic AI avatars that speak and perform naturally. Ideal for presentations, marketing videos, and branded content—no camera, studio, or actor needed.',
      link: avatarVideoProURL,
    },
  ];

  // 处理移动端滚动
  const handleScroll = () => {
    if (carouselRef.current) {
      const scrollLeft = carouselRef.current.scrollLeft;
      const itemWidth = carouselRef.current.scrollWidth / tools.length;
      const currentIndex = Math.round(scrollLeft / itemWidth);
      if (currentIndex !== activeIndex) {
        setActiveIndex(currentIndex);
      }
    }
  };

  // 处理指示器点击
  const handleIndicatorClick = (index: number) => {
    setActiveIndex(index);
    if (carouselRef.current) {
      const itemWidth = carouselRef.current.scrollWidth / tools.length;
      carouselRef.current.scrollTo({
        left: itemWidth * index,
        behavior: 'smooth',
      });
    }
  };

  // 渲染单个工具卡片
  const renderToolCard = (tool: Tool) => (
    <Link
      href={tool.link}
      className="block rounded-2xl overflow-hidden h-full flex flex-col"
    >
      {/* 工具图片 */}
      <div className="relative w-full h-[158px] rounded-2xl overflow-hidden">
        <Image
          src={tool.imageUrl}
          alt={tool.title}
          fill
          className="object-cover"
          loading="lazy"
        />
      </div>

      {/* 工具内容 */}
      <div className="p-6 pad:p-8 !pl-0 flex-1 flex flex-col">
        <h3 className="text-xl pad:text-2xl font-bold text-[#0F1117] mb-3 pad:mb-4">
          {tool.title}
        </h3>
        <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-1">
          {tool.description}
        </p>
      </div>
    </Link>
  );

  return (
    <section className="w-full py-12 pad:py-16 sm:py-20 px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题区域 */}
        <div className="text-center mb-10 pad:mb-12 sm:mb-16">
          <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-[#0F1117] mb-3 pad:mb-4">
            Explore Our AI Tools
          </h2>
        </div>

        {/* PC 端：3 列网格布局 */}
        <div className="hidden sm:grid sm:grid-cols-3 gap-6">
          {tools.map((tool) => (
            <div key={tool.id}>{renderToolCard(tool)}</div>
          ))}
        </div>

        {/* 移动端：轮播 */}
        <div className="sm:hidden">
          {/* 轮播容器 */}
          <div
            ref={carouselRef}
            onScroll={handleScroll}
            className="flex overflow-x-auto snap-x snap-mandatory no-scrollbar"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {tools.map((tool) => (
              <div key={tool.id} className="w-full flex-shrink-0 snap-center px-2">
                {renderToolCard(tool)}
              </div>
            ))}
          </div>

          {/* 指示器 */}
          <div className="flex justify-center gap-2 mt-6">
            {tools.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => handleIndicatorClick(index)}
                className={`
                  h-2 rounded-full transition-all duration-300
                  ${activeIndex === index ? 'w-8 bg-[#3366FF]' : 'w-2 bg-gray-300'}
                `}
                aria-label={`Go to tool ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExploreToolsSection;
